import {
  ActionIcon,
  CloseButton,
  Tooltip,
  Select,
  MultiSelect,
} from '@mantine/core'

import { useMutation } from '@redwoodjs/web'

import { toast } from 'src/components/Overlays/Toast/Toast'
import { GET_DOCUMENT_TEMPLATE_PACKAGES } from 'src/graphql/documentTemplatePackages.graphql'
import { DISCONNECT_DOCUMENT_TEMPLATE } from 'src/graphql/documentTemplatePackages.graphql'
import { UPDATE_DOCUMENT_TEMPLATE } from 'src/graphql/documentTemplates.graphql'
import {
  DocumentCategory,
  DocumentCategoryLabel,
  DocumentUserStatus,
} from 'src/graphql/types/documents'
import IconCopy from 'src/icons/IconCopy'
import { formatSnakeValue } from 'src/lib/formatters'

const signerOptions = [
  { value: 'APPLICANT', label: 'Applicant' },
  { value: 'RECRUITER', label: 'Recruiter' },
]

const DocumentTemplateRow = ({ pkg, doc, refetch, setSelectedTemplateId }) => {
  const [updateDocumentTemplate] = useMutation(UPDATE_DOCUMENT_TEMPLATE, {
    refetchQueries: [GET_DOCUMENT_TEMPLATE_PACKAGES],
    onCompleted: () => {
      refetch()
      toast('Document updated', 'success')
    },
    onError: () => {
      toast('Unable to update document', 'error')
    },
  })

  const [disconnectDocument, { loading: removing }] = useMutation(
    DISCONNECT_DOCUMENT_TEMPLATE,
    {
      onCompleted: () => {
        refetch()
        toast('Document removed', 'success')
      },
      onError: () => {
        toast('Unable to remove document', 'error')
      },
    }
  )

  const handleUpdate = (field: string, value: any) => {
    updateDocumentTemplate({
      variables: {
        documentTemplateId: doc.documentTemplateId,
        input: {
          [field]: value,
        },
      },
    })
  }

  return (
    <div className="grid min-h-10 w-full grid-cols-11 items-center gap-4 px-2 py-1 text-xs hover:bg-doubleNickel-gray-50 ">
      <Tooltip label={doc.documentName}>
        <div className="col-span-2 flex flex-row items-center gap-2">
          <div
            className="truncate cursor-pointer"
            onClick={() => setSelectedTemplateId(doc.documentTemplateId)}
          >
            {doc.documentName}
          </div>
          <ActionIcon
            className="ml-auto mr-4"
            variant="subtle"
            size={14}
            color="gray"
            onClick={() => {
              navigator.clipboard.writeText(doc.documentTemplateId)
              toast('Copied to clipboard', 'success')
            }}
          >
            <IconCopy className="fill-none stroke-doubleNickel-gray-700" />
          </ActionIcon>
        </div>
      </Tooltip>
      <div className="col-span-2 flex flex-row items-center gap-2">
        <div className="truncate">{doc.signingToolRefId}</div>
        <ActionIcon
          className="ml-auto mr-4"
          variant="subtle"
          size={14}
          color="gray"
          onClick={() => {
            navigator.clipboard.writeText(doc.signingToolRefId)
            toast('Copied to clipboard', 'success')
          }}
        >
          <IconCopy className="fill-none stroke-doubleNickel-gray-700" />
        </ActionIcon>
      </div>
      <Select
        className="col-span-2"
        variant="unstyled"
        size="xs"
        searchable
        data={Object.values(DocumentCategory).map((value) => ({
          value,
          label: DocumentCategoryLabel[value],
        }))}
        value={doc.documentCategory}
        onChange={(value) => handleUpdate('documentCategory', value)}
      />
      <MultiSelect
        className="col-span-2"
        variant="unstyled"
        size="xs"
        searchable
        classNames={{
          pill: 'rounded-md bg-doubleNickel-gray-100',
          pillsList:
            'flex flex-row flex-nowrap gap-2 max-w-[300px] overflow-auto',
        }}
        data={signerOptions}
        value={doc.signers}
        onChange={(value) => handleUpdate('signers', value)}
      />
      <Select
        className="col-span-2"
        variant="unstyled"
        size="xs"
        data={Object.values(DocumentUserStatus).map((value) => ({
          value,
          label: formatSnakeValue(value),
        }))}
        value={doc.userStatus}
        onChange={(value) => handleUpdate('userStatus', value)}
      />
      <CloseButton
        className="justify-self-end"
        size={20}
        variant="subtle"
        color="gray"
        onClick={() => {
          disconnectDocument({
            variables: {
              documentTemplatePackageId: pkg.documentTemplatePackageId,
              documentTemplateId: doc.documentTemplateId,
            },
          })
        }}
      />
    </div>
  )
}

export default DocumentTemplateRow
